import React, { useState, useEffect } from 'react';
import defaultAvatar from '../../assets/images/default_avatar.svg';

interface User {
  id: number;
  uuid: string;
  firstName: string;
  lastName: string;
  role?: string;
  avatarUrl: string;
  croppedAvatarUrl: string;
}

interface Group {
  id: number;
  title: string;
  type: string;
  status: 'Active' | 'Archived';
  startDate: string;
  dueDate: string;
  description: string;
  teamMembers: number;
  members?: string[];
  teamMemberAvatars: string[];
  archived?: boolean;
  hashtag?: string;
  shared?: boolean;
  hasAccess?: boolean;
  isOwner?: boolean;
  actuallyShared?: boolean;
  sharedWith?: User[];
}

interface CurrentUser {
  id: number;
  uuid: string;
  firstName: string;
  lastName: string;
  super_admin: boolean;
}

interface GroupsProps {
  currentUser: CurrentUser;
  groups?: Group[];
}

export default function Groups({ currentUser, groups: initialGroups = [] }: GroupsProps) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [hasSharedGroups, setHasSharedGroups] = useState(false);

  const [editingDescription, setEditingDescription] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [editingContext, setEditingContext] = useState<'card' | 'modal' | null>(null);

  const [detailGroup, setDetailGroup] = useState<Group | null>(null);

  const [manageMembersGroup, setManageMembersGroup] = useState<Group | null>(null);
  const [activeTab, setActiveTab] = useState<'add' | 'current'>('add');
  const [searchMember, setSearchMember] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [currentMembers, setCurrentMembers] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);

  const [shareGroup, setShareGroup] = useState<Group | null>(null);
  const [shareSearchTerm, setShareSearchTerm] = useState('');
  const [selectedShareUsers, setSelectedShareUsers] = useState<string[]>([]);
  const [availableShareUsers, setAvailableShareUsers] = useState<User[]>([]);
  const [currentlySharedWith, setCurrentlySharedWith] = useState<User[]>([]);

  const MAX_CHARS = 120;

  useEffect(() => {
    const fetchGroups = async () => {
      setLoading(true);
      setAccessError(null);
      try {
        const response = await fetch('/groups/groups_json', { 
          headers: { 
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        const accessDeniedGroups = data.filter(g => g.shared && !g.hasAccess);
        if (accessDeniedGroups.length > 0) {
          setAccessError('You do not have the necessary permissions to access some shared groups.');
        }
        
        const accessibleGroups = data.filter(g => !g.shared || g.hasAccess);
        
        const hasShared = accessibleGroups.some(g => g.shared && g.hasAccess);
        setHasSharedGroups(hasShared);
        
        setGroups(accessibleGroups);
        setFilteredGroups(accessibleGroups);
      } catch (err) {
        console.error('Error fetching groups:', err);
        setGroups([]);
        setFilteredGroups([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, []);

  useEffect(() => {
    let filtered = [...groups];

    if (searchTerm) {
      filtered = filtered.filter(
        (g) =>
          g.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (g.description || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (activeFilter === 'Active') {
      filtered = filtered.filter((g) => !g.archived && g.status === 'Active');
    } else if (activeFilter === 'Archived') {
      filtered = filtered.filter((g) => g.archived);
    }

    setFilteredGroups(filtered);
  }, [groups, searchTerm, activeFilter]);

  const fetchGroupMembers = async (groupId: number): Promise<User[]> => {
    try {
      const response = await fetch(`/groups/${groupId}/members`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.members || [];
    } catch (err) {
      console.error('Error fetching group members:', err);
      return [];
    }
  };

  const fetchAvailableUsers = async (): Promise<User[]> => {
    try {
      const response = await fetch('/users/share_index', {
        headers: { 
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });
      const data = await response.json();
      return (data.users || []).filter(user => user.uuid !== currentUser.uuid);
    } catch (err) {
      console.error('Error fetching available users:', err);
      return [];
    }
  };

  const fetchGroupSharedUsers = async (groupId: number): Promise<User[]> => {
    try {
      const response = await fetch(`/groups/${groupId}/shared_users`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.users || [];
    } catch (err) {
      console.error('Error fetching shared users:', err);
      return [];
    }
  };

  const getStatusColor = (status: string, archived?: boolean, shared?: boolean) => {
    if (archived) {
      return 'bg-gray-100 text-gray-800';
    }
    
    switch (status) {
      case 'Active':
        return shared ? 'bg-green-50 text-green-700' : 'bg-green-100 text-green-800';
      case 'Archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCardStyle = (group: Group) => {
    if (group.shared && group.hasAccess) {
      return "bg-gray-50 shadow-lg rounded-lg overflow-hidden flex flex-col h-full min-h-[260px] border border-gray-200";
    }
    return "bg-white shadow-lg rounded-lg overflow-hidden flex flex-col h-full min-h-[260px]";
  };

  const renderStatusBadges = (group: Group) => {
    return (
      <div className="flex items-center gap-2">
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
            group.status,
            group.archived,
            group.shared
          )}`}
        >
          {group.archived ? 'Archived' : group.status}
        </span>
        {group.shared && group.hasAccess && (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            Shared
          </span>
        )}
      </div>
    );
  };

  const handleGroupClick = (group: Group) => {
    if (group.shared && !group.hasAccess) {
      setAccessError('You do not have the necessary permissions to access this group.');
      return;
    }
    setDetailGroup(group);
  };

  const handleOpenShareModal = async (group: Group) => {
    setShareGroup(group);
    setShareSearchTerm('');
    setSelectedShareUsers([]);

    const [allUsers, sharedUsers] = await Promise.all([
      fetchAvailableUsers(),
      fetchGroupSharedUsers(group.id)
    ]);

    setCurrentlySharedWith(sharedUsers);
    const sharedUserUuids = new Set(sharedUsers.map(u => u.uuid));
     const filteredUsers = allUsers.filter(user => 
      !sharedUserUuids.has(user.uuid) && user.uuid !== currentUser.uuid
    );
    setAvailableShareUsers(filteredUsers);
  };

  const handleShareUsers = async () => {
    if (!shareGroup || selectedShareUsers.length === 0) return;

    try {
      const response = await fetch(`/groups/${shareGroup.id}/share_with_users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          user_uuids: selectedShareUsers
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const usersToAdd = availableShareUsers.filter(user => selectedShareUsers.includes(user.uuid));
        setCurrentlySharedWith(prev => [...prev, ...usersToAdd]);
        setAvailableShareUsers(prev => prev.filter(user => !selectedShareUsers.includes(user.uuid)));
        setSelectedShareUsers([]);

        const updatedGroupsResponse = await fetch('/groups/groups_json', {
          headers: { 
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          credentials: 'same-origin'
        });
        
        if (updatedGroupsResponse.ok) {
          const updatedGroups = await updatedGroupsResponse.json();
          setGroups(updatedGroups);
        }

        alert(`Group shared with ${selectedShareUsers.length} user(s). They will receive email and in-app notifications.`);
      } else {
        console.error('Failed to share with users:', result);
        alert(result.error || 'Failed to share group. Please try again.');
      }
    } catch (error) {
      console.error('Error sharing group:', error);
      alert('An error occurred while sharing the group.');
    }
  };

  const handleUnshareUser = async (userUuid: string) => {
    if (!shareGroup) return;

    try {
      const response = await fetch(`/groups/${shareGroup.id}/unshare_user/${userUuid}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const userToRemove = currentlySharedWith.find(u => u.uuid === userUuid);
        if (userToRemove) {
          setCurrentlySharedWith(prev => prev.filter(u => u.uuid !== userUuid));
           if (userToRemove.uuid !== currentUser.uuid) {
            setAvailableShareUsers(prev => [...prev, userToRemove]);
          }
        }

        setTimeout(async () => {
          try {
            const updatedGroupsResponse = await fetch('/groups/groups_json', {
              headers: { 
                'Accept': 'application/json',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
              },
              credentials: 'same-origin'
            });
            
            if (updatedGroupsResponse.ok) {
              const updatedGroups = await updatedGroupsResponse.json();
              setGroups(updatedGroups);
            }
          } catch (error) {
            console.error('Error refreshing groups:', error);
          }
        }, 500);
      } else {
        console.error('Failed to unshare with user:', result);
        alert(result.error || 'Failed to unshare group. Please try again.');
      }
    } catch (error) {
      console.error('Error unsharing group:', error);
      alert('An error occurred while unsharing the group.');
    }
  };

  const toggleShareUserSelection = (uuid: string) => {
    setSelectedShareUsers(prev => 
      prev.includes(uuid) 
        ? prev.filter(id => id !== uuid)
        : [...prev, uuid]
    );
  };

  const handleStartEdit = (group: Group, inModal = false) => {
    setEditingDescription(group.id);
    setEditingContext(inModal ? 'modal' : 'card');
    setEditingText(group.description || '');
  };

  const handleSaveEdit = async (groupId: number) => {
    const trimmedText = editingText.trim();
    
    if (!trimmedText && (groups.find(g => g.id === groupId)?.description || '').trim()) {
      const confirmDelete = window.confirm('Are you sure you want to delete this description?');
      if (!confirmDelete) {
        setEditingDescription(null);
        setEditingText('');
        setEditingContext(null);
        return;
      }
    }

    try {
      const response = await fetch(`/groups/${groupId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          group: {
            description: trimmedText
          }
        })
      });

      if (response.ok) {
        setGroups(prev => prev.map(g => 
          g.id === groupId ? { ...g, description: trimmedText } : g
        ));
        
        if (detailGroup && detailGroup.id === groupId) {
          setDetailGroup(prev => prev ? { ...prev, description: trimmedText } : null);
        }
      } else {
        console.error('Failed to update description');
        alert('Failed to update description. Please try again.');
      }
    } catch (error) {
      console.error('Error updating description:', error);
      alert('An error occurred while updating the description.');
    }

    setEditingDescription(null);
    setEditingText('');
  };

  const handleCancelEdit = () => {
    setEditingDescription(null);
    setEditingText('');
    setEditingContext(null);
  };

  const handleArchive = async (groupId: number) => {
    try {
      const response = await fetch(`/groups/${groupId}/archive`, {  
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (response.ok) {
        const updatedGroupsResponse = await fetch('/groups/groups_json', {
          headers: { 
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          credentials: 'same-origin'
        });
        const updatedGroups = await updatedGroupsResponse.json();
        setGroups(updatedGroups);
        setDetailGroup((dg) => (dg && dg.id === groupId ? null : dg));
      } else {
        console.error('Failed to archive group');
      }
    } catch (error) {
      console.error('Error archiving group:', error);
    }
  };

  const handleUnarchive = async (groupId: number) => {
    try {
      const response = await fetch(`/groups/${groupId}/unarchive`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (response.ok) {
        const updatedGroupsResponse = await fetch('/groups/groups_json', {
          headers: { 
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          credentials: 'same-origin'
        });
        const updatedGroups = await updatedGroupsResponse.json();
        setGroups(updatedGroups);
        setDetailGroup((dg) => (dg && dg.id === groupId ? { ...dg, archived: false } : dg));
      } else {
        console.error('Failed to unarchive group');
      }
    } catch (error) {
      console.error('Error unarchiving group:', error);
    }
  };

  const handleOpenManageMembers = async (group: Group) => {
    setManageMembersGroup(group);
    setActiveTab(group.isOwner ? 'add' : 'current');
    setSelectedMembers([]);
    setSearchMember('');

    const [members, available] = await Promise.all([
      fetchGroupMembers(group.id),
      group.isOwner ? fetchAvailableUsers() : Promise.resolve([])
    ]);

    setCurrentMembers(members);
    if (group.isOwner) {
      const memberUuids = new Set(members.map(m => m.uuid));
      const filteredUsers = available.filter(user => 
        !memberUuids.has(user.uuid) && user.uuid !== currentUser.uuid
      );
      setAvailableUsers(filteredUsers);
    }
  };

  const toggleMemberSelection = (uuid: string) => {
    setSelectedMembers(prev => 
      prev.includes(uuid) 
        ? prev.filter(id => id !== uuid)
        : [...prev, uuid]
    );
  };

  const handleAddMembers = async () => {
    if (!manageMembersGroup || selectedMembers.length === 0) return;

    try {
      const response = await fetch(`/groups/${manageMembersGroup.id}/add_members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          member_ids: selectedMembers
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const membersToAdd = availableUsers.filter(user => selectedMembers.includes(user.uuid));
        setCurrentMembers(prev => [...prev, ...membersToAdd]);
        setAvailableUsers(prev => prev.filter(user => !selectedMembers.includes(user.uuid)));
        setSelectedMembers([]);

        if (result.errors && result.errors.length > 0) {
          console.warn('Some members could not be added:', result.errors);
          alert(result.message + '\n\nWarnings:\n' + result.errors.join('\n'));
        }

        const updatedGroupsResponse = await fetch('/groups/groups_json', {
          headers: { 
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          credentials: 'same-origin'
        });
        
        if (updatedGroupsResponse.ok) {
          const updatedGroups = await updatedGroupsResponse.json();
          setGroups(updatedGroups);
        }

      } else {
        console.error('Failed to add members:', result);
        alert(result.error || 'Failed to add members. Please try again.');
      }
    } catch (error) {
      console.error('Error adding members:', error);
      alert('An error occurred while adding members.');
    }
  };

  const handleRemoveMember = async (memberUuid) => {
    if (!manageMembersGroup) return;

    try {
      const response = await fetch(`/groups/${manageMembersGroup.id}/remove_member/${memberUuid}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const memberToRemove = currentMembers.find(m => m.uuid === memberUuid);
        if (memberToRemove) {
          setCurrentMembers(prev => prev.filter(m => m.uuid !== memberUuid));
          if (memberToRemove.uuid !== currentUser.uuid) {
            setAvailableUsers(prev => [...prev, memberToRemove]);
          }
        }

        setTimeout(async () => {
          try {
            const updatedGroupsResponse = await fetch('/groups/groups_json', {
              headers: { 
                'Accept': 'application/json',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
              },
              credentials: 'same-origin'
            });
            
            if (updatedGroupsResponse.ok) {
              const updatedGroups = await updatedGroupsResponse.json();
              setGroups(updatedGroups);
            }
          } catch (error) {
            console.error('Error refreshing groups:', error);
          }
        }, 500);
      } else {
        console.error('Failed to remove member:', result);
        alert(result.error || 'Failed to remove member. Please try again.');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      alert('An error occurred while removing member.');
    }
  };

  const renderDescription = (group: Group, inModal = false) => {
    const isEditing =
      editingDescription === group.id &&
      ((inModal && editingContext === 'modal') || (!inModal && editingContext === 'card'));

    const shouldTruncate = !inModal && group.description && group.description.length > MAX_CHARS;

    if (isEditing) {
      return (
        <div className="mt-2">
          <textarea
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            rows={inModal ? 6 : 3}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSaveEdit(group.id);
              } else if (e.key === 'Escape') {
                handleCancelEdit();
              }
            }}
            autoFocus
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => handleSaveEdit(group.id)}
              className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Save
            </button>
            <button
              onClick={handleCancelEdit}
              className="px-3 py-1 text-xs font-medium bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    if (group.description) {
      const displayText = shouldTruncate
        ? `${group.description.slice(0, MAX_CHARS)}...`
        : group.description;

      return (
        <div className="text-sm text-gray-700 mt-2">
          <span className={inModal ? 'whitespace-pre-wrap' : ''}>
            {displayText}
            {group.isOwner && (
              <button
                onClick={() => handleStartEdit(group, inModal)}
                className="text-gray-400 hover:text-gray-600 ml-1 inline-flex items-center"
                title="Edit description"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
            )}
            {shouldTruncate && (
              <button
                onClick={() => handleGroupClick(group)}
                className="text-blue-500 text-xs hover:underline ml-1"
              >
                Read more
              </button>
            )}
          </span>
        </div>
      );
    } else {
      return (
        <div className="text-sm text-gray-500 mt-2 flex items-center gap-2">
          <span>No description</span>
          {group.isOwner && (
            <button
              onClick={() => handleStartEdit(group, inModal)}
              className="text-gray-400 hover:text-gray-600"
              title="Add description"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>
          )}
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="w-full flex flex-1 mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-10">
        <div className="w-full max-w-6xl">
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading groups...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex items-center justify-center flex-1 mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-10">
      <div className="w-full max-w-6xl">
        {/* Access Error Alert */}
        {accessError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{accessError}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setAccessError(null)}
                  className="text-red-400 hover:text-red-600"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between sm:hidden mb-4">
          <div className="flex items-center gap-16">
            {!hasSharedGroups && (
              <button
                onClick={() => window.location.href = '/users/favorites'}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Back to Favorites"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h1 className="text-2xl font-semibold text-gray-900">Groups</h1>
          </div>
        </div>


        {/* Header */}
        <div className="hidden sm:flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!hasSharedGroups && (
              <button
                onClick={() => window.location.href = '/users/favorites'}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Favorites
              </button>
            )}
            <h1 className="text-3xl leading-9 font-semibold text-gray-900">Groups</h1>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-wrap items-center gap-3 mt-6">
          {['All', 'Active', 'Archived'].map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                activeFilter === filter
                  ? 'bg-blue-400 text-black'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {filter}
            </button>
          ))}
          <input
            type="text"
            placeholder="Search group..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ml-auto px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
          />
        </div>

        {/* Groups List */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 auto-rows-fr">
          {filteredGroups.map((group) => (
            <div
              key={group.id}
              className={getCardStyle(group)}
            >
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{group.title}</h2>
                  </div>
                  {renderStatusBadges(group)}
                </div>

                {renderDescription(group)}

                {group.hashtag && (
                  <p className="text-sm text-blue-500 mt-2">{group.hashtag}</p>
                )}

                <div className="flex items-center mt-3 gap-2">
                  {group.teamMemberAvatars && group.teamMemberAvatars.length > 0 && (
                    <div className="flex items-center">
                      {group.teamMemberAvatars.slice(0, 5).map((avatar, idx) => (
                        <img
                          key={idx}
                          src={avatar || defaultAvatar}
                          alt="member"
                          className="w-6 h-6 rounded-full border border-white -ml-2 first:ml-0"
                        />
                      ))}
                      {group.teamMembers > 5 && (
                        <span className="ml-2 text-xs text-gray-500">
                          +{group.teamMembers - 5}
                        </span>
                      )}
                    </div>
                  )}
                  {!group.archived && (
                    <button
                      onClick={() => handleOpenManageMembers(group)}
                      className="text-xs text-blue-500 hover:text-blue-700 font-medium"
                    >
                      Manage Members
                    </button>
                  )}
                </div>

                <div className="mt-auto pt-4 border-t border-gray-200 flex justify-between">
                  {!group.archived ? (
                    <>
                      {group.isOwner && (
                        <button
                          onClick={() => handleArchive(group.id)}
                          className="flex items-center px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="mr-2"
                          >
                            <rect width="20" height="5" x="2" y="3" rx="1" />
                            <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
                            <path d="M10 12h4" />
                          </svg>
                          Archive
                        </button>
                      )}
                      
                      {group.isOwner ? (
                        <button
                          onClick={() => handleOpenShareModal(group)}
                          className="flex items-center px-3 py-1 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="mr-2"
                          >
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          {group.actuallyShared ? 'Manage Sharing' : 'Share'}
                        </button>
                      ) : (
                        <button
                          onClick={() => navigator.clipboard.writeText(`${window.location.origin}/groups/${group.id}`)}
                          className="flex items-center px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="mr-2"
                          >
                            <path d="M12 2v13" />
                            <path d="m16 6-4-4-4 4" />
                            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                          </svg>
                          Copy Link
                        </button>
                      )}
                    </>
                  ) : (
                    group.isOwner && (
                      <button
                        onClick={() => handleUnarchive(group.id)}
                         className="flex items-center px-3 py-1 text-sm font-medium text-green-700 bg-green-100 border border-gray-300 rounded-lg hover:bg-green-200 transition-colors duration-200"
                        >
                        Unarchive
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty state */}
        {filteredGroups.length === 0 && !loading && (
          <div className="text-center py-16">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No groups found</h3>
            <p className="text-gray-500 mb-4">No groups match your current filters</p>
          </div>
        )}

        {/* Detail Modal */}
        {detailGroup && (
          <div
            className="fixed inset-0 z-60 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
            onClick={() => setDetailGroup(null)}
          >
            <div
              className="bg-white rounded-xl shadow-lg w-full max-w-3xl p-6 overflow-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setDetailGroup(null)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="flex items-start justify-between mr-8">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">{detailGroup.title}</h2>
                  <p className="text-sm text-gray-600">{detailGroup.type}</p>
                </div>
                {renderStatusBadges(detailGroup)}
              </div>

              <div className="mt-4">
                <strong className="text-sm text-gray-900">Description:</strong>
                {renderDescription(detailGroup, true)}
              </div>

              {detailGroup.hashtag && (
                <p className="text-sm text-blue-500 mt-4">{detailGroup.hashtag}</p>
              )}

              {detailGroup.teamMemberAvatars && detailGroup.teamMemberAvatars.length > 0 && (
                <div className="flex items-center mt-4">
                  {detailGroup.teamMemberAvatars.map((avatar, idx) => (
                    <img
                      key={idx}
                      src={avatar || defaultAvatar}
                      alt="member"
                      className="w-8 h-8 rounded-full border border-white -ml-2 first:ml-0"
                    />
                  ))}
                </div>
              )}

              <div className="mt-6 flex justify-end gap-3">
                {!detailGroup.isOwner && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/groups/${detailGroup.id}`);
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                  >
                    Copy Link
                  </button>
                )}

                {detailGroup.isOwner && detailGroup.archived && (
                  <button
                    onClick={() => {
                      handleUnarchive(detailGroup.id);
                      setDetailGroup(null);
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors duration-200"
                  >
                    Unarchive
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Share Management Modal */}
        {shareGroup && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[80vh] overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Share Group</h2>
                  <p className="text-sm text-gray-500 mt-1">{shareGroup.title}</p>
                </div>
                <button
                  onClick={() => setShareGroup(null)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Side - Add Users */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Share with Users</h3>
                    
                    {/* Search */}
                    <div className="mb-4">
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={shareSearchTerm}
                        onChange={(e) => setShareSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Available Users */}
                    <div className="max-h-80 overflow-y-auto space-y-2 border rounded-lg p-4">
                      {availableShareUsers
                        .filter((user) =>
                          `${user.firstName} ${user.lastName} ${user.role || ''}`
                            .toLowerCase()
                            .includes(shareSearchTerm.toLowerCase())
                        )
                        .map((user) => (
                          <div
                            key={user.uuid}
                            onClick={() => toggleShareUserSelection(user.uuid)}
                            className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors duration-200 ${
                              selectedShareUsers.includes(user.uuid)
                                ? 'bg-blue-50 border-blue-300'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <img
                                src={user.croppedAvatarUrl || defaultAvatar}
                                alt={user.firstName}
                                className="w-8 h-8 rounded-full"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {user.firstName} {user.lastName}
                                </p>
                                {user.role && (
                                  <p className="text-xs text-gray-500">{user.role}</p>
                                )}
                              </div>
                            </div>
                            {selectedShareUsers.includes(user.uuid) && (
                              <span className="text-blue-500 font-bold">✓</span>
                            )}
                          </div>
                        ))}
                      {availableShareUsers.filter((user) =>
                        `${user.firstName} ${user.lastName} ${user.role || ''}`
                          .toLowerCase()
                          .includes(shareSearchTerm.toLowerCase())
                      ).length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <p className="text-sm">No available users found</p>
                        </div>
                      )}
                    </div>

                    {/* Add Button */}
                    {selectedShareUsers.length > 0 && (
                      <div className="mt-4">
                        <button
                          onClick={handleShareUsers}
                          className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                        >
                          Share with {selectedShareUsers.length} User{selectedShareUsers.length !== 1 ? 's' : ''}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Right Side - Currently Shared Users */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Currently Shared With ({currentlySharedWith.length})
                    </h3>
                    
                    <div className="max-h-80 overflow-y-auto space-y-2 border rounded-lg p-4">
                      {currentlySharedWith.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <p className="text-sm">Group not shared with anyone yet</p>
                          <p className="text-xs text-gray-400 mt-1">Select users from the left to share this group</p>
                        </div>
                      ) : (
                        currentlySharedWith.map((user) => (
                          <div
                            key={user.uuid}
                            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-green-50"
                          >
                            <div className="flex items-center gap-3">
                              <img
                                src={user.croppedAvatarUrl || defaultAvatar}
                                alt={user.firstName}
                                className="w-8 h-8 rounded-full"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {user.firstName} {user.lastName}
                                </p>
                                {user.role && (
                                  <p className="text-xs text-gray-500">{user.role}</p>
                                )}
                                <p className="text-xs text-green-600">Has access</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleUnshareUser(user.uuid)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                              title="Remove access"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
                  {currentlySharedWith.length > 0 && (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                      </svg>
                      <span>Shared users will receive email and in-app notifications</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShareGroup(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Members Management Modal */}
        {manageMembersGroup && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Manage Members</h2>
                  <p className="text-sm text-gray-500 mt-1">{manageMembersGroup.title}</p>
                </div>
                <button
                  onClick={() => setManageMembersGroup(null)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                {manageMembersGroup?.isOwner && (
                <button
                  onClick={() => setActiveTab('add')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'add'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Add Members
                </button>
                )}
                <button
                  onClick={() => setActiveTab('current')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'current'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Current Members ({currentMembers.length})
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Search */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={searchMember}
                    onChange={(e) => setSearchMember(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Add Members Tab */}
                {activeTab === 'add' && manageMembersGroup?.isOwner && (
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {availableUsers
                      .filter((user) =>
                        `${user.firstName} ${user.lastName} ${user.role || ''}`
                          .toLowerCase()
                          .includes(searchMember.toLowerCase())
                      )
                      .map((user) => (
                        <div
                          key={user.uuid}
                          onClick={() => toggleMemberSelection(user.uuid)}
                          className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors duration-200 ${
                            selectedMembers.includes(user.uuid)
                              ? 'bg-blue-50 border-blue-300'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <img
                              src={user.croppedAvatarUrl || defaultAvatar}
                              alt={user.firstName}
                              className="w-8 h-8 rounded-full"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {user.firstName} {user.lastName}
                              </p>
                              {user.role && (
                                <p className="text-xs text-gray-500">{user.role}</p>
                              )}
                            </div>
                          </div>
                          {selectedMembers.includes(user.uuid) && (
                            <span className="text-blue-500 font-bold">✓</span>
                          )}
                        </div>
                      ))}
                    {availableUsers.filter((user) =>
                      `${user.firstName} ${user.lastName} ${user.role || ''}`
                        .toLowerCase()
                        .includes(searchMember.toLowerCase())
                    ).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">No available members found</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Current Members Tab */}
                {activeTab === 'current' && (
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {currentMembers
                      .filter((user) =>
                        `${user.firstName} ${user.lastName} ${user.role || ''}`
                          .toLowerCase()
                          .includes(searchMember.toLowerCase())
                      )
                      .map((user) => (
                        <div
                          key={user.uuid}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <img
                              src={user.croppedAvatarUrl || defaultAvatar}
                              alt={user.firstName}
                              className="w-8 h-8 rounded-full"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {user.firstName} {user.lastName}
                              </p>
                              {user.role && (
                                <p className="text-xs text-gray-500">{user.role}</p>
                              )}
                            </div>
                          </div>
                          {manageMembersGroup?.isOwner && (
                            <button
                              onClick={() => handleRemoveMember(user.uuid)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                              title="Remove member"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    {currentMembers.filter((user) =>
                      `${user.firstName} ${user.lastName} ${user.role || ''}`
                        .toLowerCase()
                        .includes(searchMember.toLowerCase())
                    ).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">No current members found</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
                  {currentMembers.length} total members in group
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setManageMembersGroup(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                  {activeTab === 'add' && selectedMembers.length > 0 && manageMembersGroup?.isOwner && (
                    <button
                      onClick={handleAddMembers}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                      Add {selectedMembers.length} Member{selectedMembers.length !== 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}